/* eslint-disable react/react-in-jsx-scope */
import { memo, useEffect } from 'react';
import { Helmet } from 'react-helmet';

import { useAdobePageAnalytics } from '../hooks';

export type DataLayer = Record<string, string | ((args: any) => void)>;
type AdobeAnalyticsProps = {
  path: string;
  locale: string;
  title?: string;
};

const getSubcategory = segments => {
  const segment = segments[1];
  switch (segment) {
    case 'enterprise':
      return 'enterprise';
    case 'company':
    case 'press':
    case 'events':
    case 'news':
      return 'corporate';
    case '':
      return 'landing';
    default:
      return 'shopper';
  }
};

const screenNameMap = {
  '/': 'home',
  '/products/xumo-tv': 'xumo tv',
  '/company': 'about',
  '/press': 'press',
  '/enterprise': 'enterprise home',
  '/enterprise/contant': 'contact us',
  '/enterprise/cms': 'enterprise cms',
  '/nascar-partnership': 'nascar partnership',
  '/nascar-partnership/signup': 'nascar partnership signup',
};

// TODO: pull from CMS
const getScreenName = path => {
  const pathSegments = path.split('/');
  const pathString = pathSegments.slice(1).join('/');

  const screenName = screenNameMap['/' + pathString];

  return screenName || (pathString.indexOf('press') >= 0 ? pathSegments.slice(-1)[0].split('-').join(' ') : 'page');
};

const Analytics = ({ path, locale, title }: AdobeAnalyticsProps) => {
  const getModalAnalytics = path => {
    try {
      const modalList = ['/products/xumo-tv'];
      const pathSegments = path.split('/');
      const pathString = pathSegments.slice(1).join('/');
      const truePath = '/' + pathString;

      const modalKey = Object.keys(localStorage).find(key => key.endsWith('-prospectModalHiddenUntil'));
      const modalValue = modalKey ? localStorage.getItem(modalKey) : null;
      const now = new Date().getTime();

      return (modalList.includes(truePath) && modalValue && parseInt(modalValue) < now) || (modalList.includes(truePath) && !modalValue) ? 'modal' : null;
    } catch (e) {
      console.error('Error accessing localStorage:', e);
      return null;
    }
  };

  const pathSegments = path.split('/');
  const businessType = 'resi';
  const siteType = 'sales';
  const screenName = getScreenName(path);

  const primaryCategory = 'xumo';
  const subCategory1 = getSubcategory(pathSegments);
  const subCategory2 = '';
  //const modalAnalytics = getModalAnalytics(path);
  //const pageName = `${businessType}|${siteType}|${primaryCategory}|${subCategory1}|${screenName}${modalAnalytics ? `|${modalAnalytics}` : ''}`;
  const pageName = `${businessType}|${siteType}|${primaryCategory}|${subCategory1}|${screenName}`;

  const dataLayer: DataLayer = {
    category: JSON.stringify({
      category: {
        primaryCategory,
        subCategory1,
        subCategory2,
        businessType,
        siteType,
        designType: 'responsive',
      },
    }),
    affiliate: JSON.stringify({
      affiliate: {
        channel: 'web',
        name: 'comcast',
      },
    }),
    pageInfo: JSON.stringify({
      pageInfo: {
        language: locale,
        pageName,
        screenName,
      },
    }),
    codeBase: JSON.stringify({
      codebase: {
        name: 'Xumo marketing page',
        releaseVersion: '1.0.0',
      },
    }),
  };

  // Handles route changes
  useAdobePageAnalytics(title);

  // Handle form submission tracking
  useEffect(() => {
    const handleFormSuccessEvent = (e: CustomEvent) => {
      // Access the form data from the event detail
      const { formName, formData, page, buttonText } = e.detail;

      // Send form submission tracking data to Adobe Analytics using Alloy
      if (window.alloy && typeof window.alloy === 'function') {
        window.alloy('sendEvent', {
          xdm: {
            eventType: 'formSubmit',
            eventMethod: 'form-Submit',
            eventName: buttonText || formName,
            eventAction: 'success',
            eventPage: pageName,
            popupName: formName,
            formPage: page,
            formData,
            timestamp: new Date().toISOString(),
          },
        });

        console.log('Form submission tracked and sent via Alloy:', { formName, page, buttonText });
      }

      // Also send data to the digitalData object
      if (window.digitalData && window.digitalData.event && formName && page) {
        window.digitalData.event.push({
          eventInfo: {
            eventType: 'formSubmit',
            eventMethod: 'form-Submit',
            eventName: buttonText || formName,
            eventAction: 'success',
            eventPage: pageName,
            popupName: formName,
            formPage: page,
            formData,
            timestamp: new Date().toISOString(),
          },
        });
        console.log('Form submission data pushed to digitalData:', { formName, page, buttonText });
      }

      // Also send data to the dataLayer object
      if (window.dataLayer && Array.isArray(window.dataLayer) && formName && page) {
        window.dataLayer.push({
          eventType: 'formSubmit',
          eventMethod: 'form-Submit',
          eventName: buttonText || formName,
          eventAction: 'success',
          eventPage: pageName,
          popupName: formName,
          formPage: page,
          formData,
          timestamp: new Date().toISOString(),
        });
        console.log('Form submission data pushed to dataLayer:', { formName, page, buttonText });
      }
    };

    // Add event listener for our custom form success event
    document.addEventListener('c-tracking-log-form-success', handleFormSuccessEvent as EventListener);

    // Clean up event listener on component unmount
    return () => {
      document.removeEventListener('c-tracking-log-form-success', handleFormSuccessEvent as EventListener);
    };
  }, []);

  // Handle form error tracking
  useEffect(() => {
    const handleFormErrorEvent = (e: CustomEvent) => {
      // Access the error data from the event detail
      const { formName, errorMessage, page } = e.detail;

      // Send form error tracking data to Adobe Analytics using Alloy
      if (window.alloy && typeof window.alloy === 'function') {
        window.alloy('sendEvent', {
          xdm: {
            eventMethod: 'send-Error',
            errorMessage,
            eventPage: pageName,
            formName,
            formPage: page,
            timestamp: new Date().toISOString(),
          },
        });

        console.log('Form error tracked and sent via Alloy:', { formName, errorMessage, page, pageName });
      }

      // Also send data to the digitalData object
      if (window.digitalData && window.digitalData.event) {
        window.digitalData.event.push({
          eventInfo: {
            eventMethod: 'send-Error',
            errorMessage,
            eventPage: pageName,
            formName,
            formPage: page,
            timestamp: new Date().toISOString(),
          },
        });
        console.log('Form error data pushed to digitalData:', { formName, errorMessage, page, pageName });
      }

      // Also send data to the dataLayer object
      if (window.dataLayer && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          eventMethod: 'send-Error',
          errorMessage,
          eventPage: pageName,
          formName,
          formPage: page,
          timestamp: new Date().toISOString(),
        });
        console.log('Form error data pushed to dataLayer:', { formName, errorMessage, page, pageName });
      }
    };

    // Add event listener for our custom form error event
    document.addEventListener('c-tracking-log-form-error', handleFormErrorEvent as EventListener);

    // Clean up event listener on component unmount
    return () => {
      document.removeEventListener('c-tracking-log-form-error', handleFormErrorEvent as EventListener);
    };
  }, [pageName]);

  // Handle form popup tracking
  useEffect(() => {
    const handleFormPopupEvent = (e: CustomEvent) => {
      // Access the form data from the event detail
      const { popupName, formName, page } = e.detail;

      // Send form submission tracking data to Adobe Analytics using Alloy
      if (window.alloy && typeof window.alloy === 'function') {
        window.alloy('sendEvent', {
          xdm: {
            eventType: 'formPopup',
            eventMethod: 'component-Track',
            eventName: 'popup Impression',
            eventPage: pageName,
            popupName: popupName,
            formName,
            formPage: page,
            timestamp: new Date().toISOString(),
          },
        });
        console.log('Form popup tracked and sent via Alloy:', { formName, page });
      }

      // Also send data to the digitalData object
      if (window.digitalData && window.digitalData.event && formName && page) {
        window.digitalData.event.push({
          eventInfo: {
            eventType: 'formPopup',
            eventMethod: 'component-Track',
            eventName: 'popup Impression',
            eventPage: pageName,
            popupName: popupName,
            formName,
            formPage: page,
            timestamp: new Date().toISOString(),
          },
        });
        console.log('Form popup tracked pushed to digitalData:', { formName, page });
      }

      // Also send data to the dataLayer object
      if (window.dataLayer && Array.isArray(window.dataLayer) && formName && page) {
        window.dataLayer.push({
          eventType: 'formPopup',
          eventMethod: 'component-Track',
          eventName: 'popup Impression',
          eventPage: pageName,
          popupName: popupName,
          formName,
          formPage: page,
          timestamp: new Date().toISOString(),
        });
        console.log('Form popup tracked pushed to dataLayer:', { formName, page });
      }
    };

    // Add event listener for our custom form popup event
    document.addEventListener('c-tracking-log-form-popup', handleFormPopupEvent as EventListener);

    // Clean up event listener on component unmount
    return () => {
      document.removeEventListener('c-tracking-log-form-popup', handleFormPopupEvent as EventListener);
    };
  }, [pageName]);

  return (
    <Helmet>
      <script
        key="comcast-tracking-schema"
        id={'schema-version'}
        type="tracking-data-digitalData"
        dangerouslySetInnerHTML={{ __html: `{ schemaVersion: '0.21.1' }` }}
        async
      />
      {!!Object.keys(dataLayer).length &&
        Object.keys(dataLayer).map((key: keyof DataLayer, index) => {
          if (!(typeof dataLayer[key] === 'string')) {
            return null;
          }
          return (
            <script key={'comcast-data-layer-' + index} id={`data-page-${key}`} type="tracking-data-page">
              {dataLayer[key]}
            </script>
          );
        })}
    </Helmet>
  );
};

export const AdobeAnalytics = memo(Analytics);

///export default AdobeAnalytics;
